import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Checkbox,
  IconButton,
} from '@mui/material';
import { MoreVert, CheckCircle, Warning, Block } from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface UsersTableProps {
  users: any[];
  selectedUsers: string[];
  onToggleSelection: (userId: string) => void;
  onSelectAll: (checked: boolean) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, user: any) => void;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  selectedUsers,
  onToggleSelection,
  onSelectAll,
  onMenuOpen,
}) => {
  const { t } = useTranslation();

  const getUserStatus = (user: any) => {
    switch (user.status) {
      case 'active':
        return <Chip label={t('warranty.active')} color="success" size="small" icon={<CheckCircle />} />;
      case 'suspended':
        return <Chip label={t('warranty.pending')} color="warning" size="small" icon={<Warning />} />;
      case 'deactivated':
        return <Chip label={t('admin.deactivated')} color="error" size="small" icon={<Block />} />;
      default:
        return <Chip label={user.status} size="small" />;
    }
  };

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={users.length > 0 && selectedUsers.length === users.length}
                onChange={(e) => onSelectAll(e.target.checked)}
              />
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>{t('admin.user')}</TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, minWidth: 200 }}>{t('common.email')}</TableCell>
            <TableCell sx={{ minWidth: 80 }}>{t('auth.role')}</TableCell>
            <TableCell sx={{ minWidth: 100 }}>{t('common.status')}</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, minWidth: 80 }}>{t('nav.orders')}</TableCell>
            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, minWidth: 100 }}>{t('cart.total')}</TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, minWidth: 100 }}>{t('orders.created')}</TableCell>
            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, minWidth: 100 }}>{t('admin.lastLogin')}</TableCell>
            <TableCell align="right" sx={{ minWidth: 80 }}>{t('common.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedUsers.includes(user.id)}
                  onChange={() => onToggleSelection(user.id)}
                />
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">
                    {user.profile?.full_name || t('common.na')}
                  </Typography>
                  {user.profile?.company_name && (
                    <Typography variant="caption" color="text.secondary">
                      {user.profile.company_name}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{user.email}</TableCell>
              <TableCell>
                <Chip 
                  label={user.role} 
                  size="small" 
                  color={user.role === 'admin' ? 'primary' : 'default'}
                />
              </TableCell>
              <TableCell>{getUserStatus(user)}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{user.activity_summary?.total_orders || 0}</TableCell>
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                €{user.activity_summary?.total_spent?.toFixed(2) || '0.00'}
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                {format(new Date(user.created_at), 'MMM dd, yyyy')}
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                {user.last_sign_in
                  ? format(new Date(user.last_sign_in), 'MMM dd, yyyy')
                  : t('admin.never')}
              </TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  onClick={(e) => onMenuOpen(e, user)}
                >
                  <MoreVert />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UsersTable;